import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { isDeploymentRunBusy } from "@/types/workshop.type";
import { useWorkshopArtifacts } from "./useWorkshopArtifacts";
import { useWorkshopContextItems } from "./useWorkshopContextItems";
import { useWorkshopDeployments } from "./useWorkshopDeployments";
import { useWorkshopFiles } from "./useWorkshopFiles";
import { useWorkshopIdeas } from "./useWorkshopIdeas";
import { useWorkshopMedia } from "./useWorkshopMedia";
import {
  workshopContextKey,
  workshopDeploymentsKey,
} from "./workshopKeys";
import { isOutputPicture } from "../workshopOutputs";

export type WorkshopRoomCounts = {
  /**
   * Documents, ideas and generated pictures: what the room's Outputs lists and
   * the Projects home counts (workshopOutputs, isOutputPicture). Null until known.
   */
  outputs: number | null;
  /** Pins, files and the pictures that are not outputs (uploads, library copies). */
  context: number | null;
  /** Deployed agents: the room's Progress section. */
  progress: number | null;
  /** A run is going now (the one thing green may say). */
  progressLive: boolean;
};

/**
 * The counts beside the thread head's room entries. They count exactly what
 * the Project room lists under each heading (WorkshopSidePanel), through the
 * same queries, so an entry never promises a number its section does not show.
 * A count whose read failed is left out (null), never shown as zero.
 */
export function useWorkshopRoomCounts(projectId: string | null): WorkshopRoomCounts {
  const qc = useQueryClient();
  const artifacts = useWorkshopArtifacts(projectId, false);
  const ideas = useWorkshopIdeas(projectId, false);
  const media = useWorkshopMedia(projectId);
  const files = useWorkshopFiles(projectId);
  const context = useWorkshopContextItems(projectId);
  const { deployments, isLoading: deploymentsLoading } = useWorkshopDeployments(projectId);

  // The context and deployment hooks hand back [] on a failed read; the cache
  // says whether that list is real.
  const known = (key: QueryKey, loading: boolean) =>
    !loading && qc.getQueryState(key)?.status === "success";

  const generated = media.data ? media.data.filter(isOutputPicture).length : null;
  const outputs =
    artifacts.data && ideas.data && generated !== null
      ? artifacts.data.length + ideas.data.length + generated
      : null;
  const contextCount =
    media.data && generated !== null && files.data && known(workshopContextKey(projectId), context.isLoading)
      ? media.data.length - generated + files.data.length + context.items.length
      : null;
  const progressKnown = known(workshopDeploymentsKey(projectId), deploymentsLoading);

  return {
    outputs,
    context: contextCount,
    progress: progressKnown ? deployments.length : null,
    progressLive: progressKnown && deployments.some((d) => isDeploymentRunBusy(d.lastRunStatus)),
  };
}
